import { format, getYear, getMonth, getDate, endOfDay, subDays, startOfDay, addDays, isSameDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { programOperatingDays } from './programOperatingDays';

export function toDateString(value: Date) {
    if (value === null) {
        return null;
    }

    const offset = value.getTimezoneOffset()
    const nextDate = new Date(value.getTime() - (offset * 60 * 1000))
    return nextDate.toISOString().split('T')[0];
}

export function toDateTimeString(value: Date) {
    if (value === null) {
        return null;
    }

    const offset = value.getTimezoneOffset()
    const nextDate = new Date(value.getTime() - (offset * 60 * 1000))
    return nextDate.toISOString().split('Z')[0];
}

export function dateEqualityComparer(x: Date, y: Date) {
    if (!x && !y) {
        return true;
    }

    if (!x || !y) {
        return false;
    }

    return x.getTime() === y.getTime();
}

export function tryParseInt<T>(value: string, valueIfParseFailed: T) {
    if (stringIsNullOrEmpty(value)) {
        return valueIfParseFailed;
    }

    const result = parseInt(value, 10);

    // Check if the result is a valid number and not NaN
    if (!isNaN(result)) {
        return result;
    }

    return valueIfParseFailed;
}

export function tryParseFloat<T>(value: string, valueIfParseFailed: T) {
    if (stringIsNullOrEmpty(value)) {
        return valueIfParseFailed;
    }

    const result = parseFloat(value);

    // Check if the result is a valid number and not NaN
    if (!isNaN(result)) {
        return result;
    }

    return valueIfParseFailed;
}

export function tryParseDate<T>(value: string, valueIfParseFailed: T) {
    if (stringIsNullOrEmpty(value)) {
        return valueIfParseFailed;
    }

    const result = new Date(value);

    // Check if the result is a valid number and not NaN
    if (!isNaN(result.valueOf())) {
        return result;
    }

    return valueIfParseFailed;
}

export function stringIsNullOrEmpty(value: string | null | undefined) {
    return value === null || value === undefined || value.trim() === '';
}


export function updateDatePreservingTime(existingDate: Date, incomingDate: Date) {
    // Extract the date parts from the incoming date
    const year = getYear(incomingDate);
    const month = getMonth(incomingDate);
    const day = getDate(incomingDate);

    // Extract time components from the existing date
    const hours = existingDate.getHours();
    const minutes = existingDate.getMinutes();
    const seconds = existingDate.getSeconds();
    const milliseconds = existingDate.getMilliseconds();

    // Create a new date with the incoming date parts and existing time components
    const updatedDate = new Date(year, month, day, hours, minutes, seconds, milliseconds);

    return updatedDate;
}

export function lastNanosecondOfPreviousDay(date: Date) {
    // Step 1: Get the previous day using date-fns
    const previousDay = subDays(date, 1);

    // Step 2: Set the time to the end of that day
    return endOfDay(previousDay);
}

export function roundCurrency(value: number) {
    return Math.round(value * 100) / 100;
}

export function countCalendarDaysBetween(x: Date, y: Date): number {
    let days = 0;
    const start = startOfDay(x);
    const end = startOfDay(y);
    let current = start;

    while (current <= end) {
        current = addDays(current, 1); // Use date-fns to add 1 day
        days++;
    }

    return days;
}

export function countRoomNightsBetween(x: Date, y: Date): number {
    const daysBetween = countCalendarDaysBetween(x, y) - 1;

    if (daysBetween >= 0) {
        return daysBetween;
    }

    return 0;
}

export function countProgramDaysBetween(x: Date, y: Date): number {
    return programOperatingDays(x, y).days;
}

export function longToTHB(value: number) {
    return decimalToTHB(value / 100);
}

export function longToMoney(value: number) {
    return (value / 100).toLocaleString('en-NZ', { minimumFractionDigits: 2 });
}

export function longToTHBWithPrefix(value: number) {
    return decimalToTHBWithPrefix(value / 100);
}

export function decimalToTHB(value: number) {
    return value.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' });
}

export function decimalToTHBWithPrefix(value: number) {
    // prefix with baht symbol instead of THB suffix
    return '฿' + value.toLocaleString('en-NZ', { minimumFractionDigits: 2 });
}

export function areDatesSameDayInBKKTime(date1: Date | string | number, date2: Date | string | number) {
    // Convert both dates to the given timezone
    const zonedDate1 = toZonedTime(date1, 'Asia/Bangkok');
    const zonedDate2 = toZonedTime(date2, 'Asia/Bangkok');

    // Compare if they are on the same day
    return isSameDay(zonedDate1, zonedDate2);
}

export function isRevenueLocked(revenueLockDate: Date | string | null, revenueDate: Date | string) {
    if (!revenueLockDate) {
        return false;
    }

    // Convert both dates to the given timezone
    const zonedRevenueLockDate = startOfDay(toZonedTime(revenueLockDate, 'Asia/Bangkok'));
    const zonedRevenueDate = startOfDay(toZonedTime(revenueDate, 'Asia/Bangkok'));

    return zonedRevenueDate <= zonedRevenueLockDate;
}

export function getRevenueLockDateInputMin(revenueLockDate: Date | null) {
    if (!revenueLockDate) {
        return null;
    }

    return format(startOfDay(toZonedTime(revenueLockDate, 'Asia/Bangkok')), 'yyyy-MM-dd');
}




export type GroupableDateRange = { start: Date; end: Date };

/**
 * Groups date ranges into consecutive, non-overlapping groups with minimal gaps.
 *
 * @param ranges - Array of date ranges with start and end dates.
 * @returns An array of groups, each containing non-overlapping date ranges.
 */
export function groupConsecutiveRanges(ranges: GroupableDateRange[]): GroupableDateRange[][] {
    if (ranges.length === 0) return [];

    // Step 1: Sort ranges by start time, breaking ties by end time
    ranges.sort((a, b) => a.start.getTime() - b.start.getTime() || a.end.getTime() - b.end.getTime());

    // Step 2: Use a priority queue (min-heap) to track groups' end times
    const groups: GroupableDateRange[][] = [];
    const groupEndTimes: Date[] = [];

    for (const range of ranges) {
        let placed = false;

        // Try to place the range in the group with the earliest end time that can accommodate it
        for (let i = 0; i < groupEndTimes.length; i++) {
            if (groupEndTimes[i] <= range.start) {
                // Place the range in this group
                groups[i].push(range);
                groupEndTimes[i] = range.end; // Update the end time of this group
                placed = true;
                break;
            }
        }

        // If not placed, create a new group
        if (!placed) {
            groups.push([range]);
            groupEndTimes.push(range.end);
        }
    }

    return groups;
}
